/**
 * BracketDetector — single source of truth for identifying conditional
 * bracket-child stops vs. live protective stops.
 *
 * Schwab nests bracket children as TRIGGER → OCO → [STOP, LIMIT] and
 * leaves child stops at PENDING_ACTIVATION even after the parent BUY
 * fills, so status alone can't distinguish a conditional stop from a
 * live protective one. The reliable test is structural: is there an
 * unfilled BUY bracket parent in the orders feed whose child stop's
 * (symbol, account, price) tuple matches?
 *
 * Consumed by useWorkingOrders.js (client) and api/schwab/sync.js (server).
 */

/**
 * Flatten a tree of Schwab order strategies into a flat list of leaf orders.
 *
 * Schwab nests bracket children using two strategy types:
 *   - OCO  : pure wrapper. No orderType, just legs. Unwrap silently.
 *   - TRIGGER : a REAL order whose fill triggers conditional children.
 *               Has its own orderType (e.g. LIMIT/STOP) and orderLegCollection.
 *               Include it as a leaf AND recurse into its children — both are
 *               independently meaningful working orders.
 *
 * EF-185d: prior versions unwrapped every TRIGGER as if it were a pure wrapper.
 * That silently dropped 1/3-take-profit LIMIT orders, which Erevis builds as
 * TRIGGER(LIMIT) → replacement STOP. After fill of the LIMIT the replacement
 * activates, but until then both legs are working orders that must surface.
 *
 * SINGLE / unspecified / TRIGGER-without-children → leaf.
 */
export function flattenChildStrategies(children) {
  if (!children || children.length === 0) return []
  const result = []
  for (const child of children) {
    if (child.orderStrategyType === 'OCO') {
      // Pure wrapper. Recurse, do not include the wrapper itself.
      result.push(...flattenChildStrategies(child.childOrderStrategies || []))
    } else if (
      child.orderStrategyType === 'TRIGGER' &&
      Array.isArray(child.childOrderStrategies) &&
      child.childOrderStrategies.length > 0
    ) {
      // Real order with conditional children. Both matter — push the parent
      // as a leaf and recurse into its children.
      result.push(child)
      result.push(...flattenChildStrategies(child.childOrderStrategies))
    } else {
      // SINGLE, TRIGGER-without-children, or any other shape → leaf.
      result.push(child)
    }
  }
  return result
}

export function computeConditionalStopKeys(orders, { terminalStatuses = null } = {}) {
  const keys = new Set()
  for (const o of orders) {
    if (terminalStatuses && terminalStatuses.has(o.status)) continue
    const enrichedChildren = Array.isArray(o.childOrders) && o.childOrders.length > 0 ? o.childOrders : null
    const rawChildStrategies = o.childOrderStrategies || o._raw?.childOrderStrategies
    const rawChildren = !enrichedChildren && Array.isArray(rawChildStrategies) && rawChildStrategies.length > 0
      ? rawChildStrategies : null
    if (!enrichedChildren && !rawChildren) continue
    // Accept both adapted (flat o.side) and raw (nested orderLegCollection) shapes —
    // sync.js feeds raw orders here; client paths feed adapted ones.
    const instruction = o.side || o.orderLegCollection?.[0]?.instruction
    if (instruction !== 'BUY' && instruction !== 'BUY_TO_OPEN') continue
    const accountNumber = o.accountNumber
    if (!accountNumber) continue
    const symbol = o.symbol || o.orderLegCollection?.[0]?.instrument?.symbol?.toUpperCase()
    if (!symbol) continue
    if (enrichedChildren) {
      for (const child of enrichedChildren) {
        const childPrice = child.computedStopPrice ?? child.stopPrice
        if (childPrice == null || Number.isNaN(Number(childPrice))) continue
        if (child.orderType === 'STOP' || child.orderType === 'STOP_LIMIT') {
          keys.add(`${symbol}|${accountNumber}|${Number(childPrice).toFixed(2)}`)
        }
      }
    } else {
      const leaves = flattenChildStrategies(rawChildren)
      for (const child of leaves) {
        const childPrice = child.stopPrice
        if (childPrice == null || Number.isNaN(Number(childPrice))) continue
        if (child.orderType === 'STOP' || child.orderType === 'STOP_LIMIT') {
          keys.add(`${symbol}|${accountNumber}|${Number(childPrice).toFixed(2)}`)
        }
      }
    }
  }
  return keys
}

export function isConditionalStop(order, accountNumber, conditionalStopKeys) {
  if (order.stopPrice == null || Number.isNaN(Number(order.stopPrice))) return false
  const symbol = order.symbol || (order.orderLegCollection?.[0]?.instrument?.symbol || '').toUpperCase()
  const key = `${symbol}|${accountNumber}|${Number(order.stopPrice).toFixed(2)}`
  return conditionalStopKeys.has(key)
}
